/**
 * Active runs — the runs needing attention right now: in-flight (`running`) and
 * suspended (`awaiting_user_response`), and a failed run that can still be
 * answered (`resumable`). We fetch the three lists and merge them,
 * polling every ~3s while any live row exists so transitions surface without a
 * manual refresh, and once a minute while there is none, so a run that starts or
 * comes to need the person while the page is open still appears. Awaiting rows get a prominent "Answer →" CTA into the run
 * view; running rows link through to watch progress.
 */
import { Badge, Icon, Notice, Spinner } from '@evt/flow-stream-ui'
import type { RunSummary } from '@portal/contracts'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listFlows } from '../api/flows'
import { listAnswerableRuns, listRuns } from '../api/runs'
import { keys } from '../hooks/queryClient'
import { describeApiError, useI18n } from '../i18n'

const POLL_MS = 3_000
/** While nothing is listed, as the Home polls when nothing needs the person. */
const IDLE_POLL_MS = 60_000

/** How often the three lists are read: often while a run is listed, slowly while none is, never not at all. */
export function activeRunsPollMs(hasLive: boolean): number {
  return hasLive ? POLL_MS : IDLE_POLL_MS
}

/**
 * The three lists as one, one row per run, newest first. The answerable list is read from the Home's short-lived
 * cache while the other two are read live, so a run answered a moment ago can be in it as answerable and in the
 * running list at once: the live lists win, and a running run wins over an awaiting one, since that is the way a run
 * moves when it is answered.
 */
export function mergeActiveRuns(awaiting: RunSummary[], answerable: RunSummary[], running: RunSummary[]): RunSummary[] {
  const byId = new Map<string, RunSummary>()
  for (const run of [...answerable, ...awaiting, ...running]) byId.set(run.executionId, run)
  return [...byId.values()].sort((a, b) => +new Date(b.startedAt) - +new Date(a.startedAt))
}

export function ActiveRunsPage() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const [pollMs, setPollMs] = useState<number>(POLL_MS)

  const flows = useQuery({ queryKey: keys.flows(), queryFn: listFlows })
  const running = useQuery({
    queryKey: keys.conversations('active-running'),
    queryFn: () => listRuns({ status: 'running', limit: 50 }),
    refetchInterval: pollMs
  })
  const awaiting = useQuery({
    queryKey: keys.conversations('active-awaiting'),
    queryFn: () => listRuns({ status: 'awaiting_user_response', limit: 50 }),
    refetchInterval: pollMs
  })
  // A run whose last reply failed while it can still be answered waits on the person too. The BFF reads it from the
  // same window the Home's "needs you" list uses, so every run the Home sends here is listed.
  const answerable = useQuery({
    queryKey: keys.conversations('active-answerable'),
    queryFn: listAnswerableRuns,
    refetchInterval: pollMs
  })
  const flowNames = new Map((flows.data ?? []).map((f) => [f.flowId, f.displayName]))

  const rows = useMemo<RunSummary[]>(
    () => mergeActiveRuns(awaiting.data?.items ?? [], answerable.data?.items ?? [], running.data?.items ?? []),
    [awaiting.data, answerable.data, running.data]
  )

  const hasLive = rows.length > 0
  useEffect(() => {
    setPollMs(activeRunsPollMs(hasLive))
  }, [hasLive])

  const loading = running.isLoading || awaiting.isLoading || answerable.isLoading
  const error: unknown = running.error ?? awaiting.error ?? answerable.error

  return (
    <div className="page portal-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t.runs.activeTitle}</h1>
          <div className="page-sub">{t.runs.activeSubtitle}</div>
        </div>
        {hasLive && (
          <div className="page-actions">
            <span className="live-pulse">{t.runs.live}</span>
          </div>
        )}
      </header>

      {loading && (
        <div className="portal-loading">
          <Spinner size={18} /> {t.common.loading}
        </div>
      )}

      {error != null && <Notice kind="danger">{describeApiError(t, error)}</Notice>}

      {!loading && rows.length === 0 && <Notice kind="info">{t.runs.activeEmpty}</Notice>}

      {rows.length > 0 && (
        <div className="run-list">
          {rows.map((r) => (
            <RunRow
              key={r.executionId}
              run={r}
              displayName={flowNames.get(r.flowId) ?? r.displayName}
              onOpen={() => navigate(`/runs/${encodeURIComponent(r.executionId)}`)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function RunRow({ run, displayName, onOpen }: { run: RunSummary; displayName: string; onOpen: () => void }) {
  const { t, formatAgo } = useI18n()
  const awaiting = run.status === 'awaiting_user_response' || run.resumable !== undefined
  return (
    <div className={`run-row card${awaiting ? ' is-awaiting' : ''}`} onClick={onOpen}>
      <div className="run-row-main">
        <div className="run-row-title">{displayName}</div>
        {run.briefPreview && <div className="run-row-brief muted">{run.briefPreview}</div>}
        <div className="run-row-meta">
          <span className="chip mono">{run.executionId.slice(0, 16)}…</span>
          <span className="muted">{t.runs.startedAt(formatAgo(run.startedAt))}</span>
          {run.startedBy && <span className="muted">· {run.startedBy}</span>}
        </div>
      </div>
      <div className="run-row-right">
        {awaiting ? (
          <Badge kind="warning" dot>
            {t.runs.needsReply}
          </Badge>
        ) : (
          <span className="live-pulse">{t.runs.running}</span>
        )}
        <button
          type="button"
          className={`btn btn-sm${awaiting ? ' btn-primary' : ''}`}
          onClick={(e) => {
            e.stopPropagation()
            onOpen()
          }}
        >
          {awaiting ? t.runs.answer : t.runs.watch} <Icon name="chevron" size={13} />
        </button>
      </div>
    </div>
  )
}
